import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PaginationService } from '@services/pagination/pagination.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { SNModalService } from '@web-services/modal/snModal.service';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';
import { UpdatePackageProductModalComponent } from '@web-components/packages-product/modals/update-package-product-modal/update-package-product-modal.component';
import { Observable } from 'rxjs/Observable';
import { ProductPackageService } from '@web-services/settings/productPackage.service';
import { Subscription } from 'rxjs/Subscription';
import {
	IItemsOverviewGen,
	IModalAction,
	IProductPackageItem,
	PageRequest
} from '@models/models';

@Component({
	selector: 'product-block',
	template: require('./product-block.component.html')
})
export class ProductBlockComponent implements OnInit, OnDestroy {
	@Input() companyId: number;
	modalTitle: string;
	pageRequest: PageRequest;
	products: IProductPackageItem[] = [];
	itemsQuantity: number;
	pageNumbers: Array<number>;
	private allowedFilters: Array<string>;

	readonly MODAL_TITLES: Array<string> = [
		'Add New Product',
		'Duplicate Product',
		'Edit Product'
	];

	readonly PRODUCTS_REQUEST_DEFAULT: PageRequest = {
		filterBy: 'add_ons',
		paging: {
			pageNumber: 0,
			pageSize: 10,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	readonly PRODUCT_DEFAULT: IProductPackageItem = {
		name: '',
		taxType: 'INCLUDED',
		type: 'ADD_ON',
		description: ''
	};

	modalDataSubscription: Subscription;
	selectedPackageModalRef: NgbModalRef;

	constructor(
		private productPackagesDataService: ProductPackagesDataServiceProvider,
		private productPackageService: ProductPackageService,
		private messageService: MessageService,
		private paginationService: PaginationService,
		private modalService: SNModalService
	) {}

	ngOnInit(): void {
		this.applyPageProductFromLocalStorage();
		this.getProductsData();
		this.productPackagesDataService.updateProductPackageSubject
			.debounceTime(500)
			.subscribe((): void => {
				this.pageRequest.paging.searchToken = null;
				this.pageRequest.paging.pageNumber = 0;
				this.getProductsData();
			});
	}

	ngOnDestroy(): void {
		this.productPackagesDataService.updateProductPackageSubject.complete();
	}

	isFilterAllowed(filterName: string): boolean {
		if (!this.allowedFilters) {
			return false;
		}
		return this.allowedFilters.includes(filterName);
	}

	orderByField(filter: string): void {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getProductsData();
	}

	openEditProductModal(product: IProductPackageItem): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			UpdatePackageProductModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = this.MODAL_TITLES[2];
		this.selectedPackageModalRef.componentInstance.name = 'Product';
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(product)
		);
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
	}

	openDuplicateProductModal(product: IProductPackageItem): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			UpdatePackageProductModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = this.MODAL_TITLES[1];
		this.selectedPackageModalRef.componentInstance.name = 'Product';
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(product)
		);
	}

	openAddNewProductModal(): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			UpdatePackageProductModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = this.MODAL_TITLES[0];
		this.selectedPackageModalRef.componentInstance.name = 'Product';
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(this.PRODUCT_DEFAULT)
		);
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
	}

	openDeleteProductModal(product: IProductPackageItem): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			DeleteItemModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = 'Product';
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(product)
		);
		this.selectedPackageModalRef.componentInstance.itemName = product.name;
	}

	updateProductData(pageRequest: PageRequest): void {
		this.pageRequest = pageRequest;
		this.getProductsData();
	}

	convertProductToPackage(product: IProductPackageItem): void {
		const body = {
			productTemplateId: product.id,
			requiredProductType: 'PACKAGE'
		};
		this.productPackagesDataService
			.convertProductPackage(body)
			.subscribe((): void => {
				this.productPackagesDataService.updateProductPackageSubject.next(
					'updatePackages'
				);
				this.messageService.showToaster(
					's_9',
					'productsPackages',
					product.name
				);
			});
	}

	private productModalActions({
		action,
		props
	}: IModalAction<IProductPackageItem>): void {
		this.selectedPackageModalRef.componentInstance.btnDisabled = true;

		switch (action) {
			case 'Add New Product':
				this.addNewProduct(props).subscribe(
					(data: IProductPackageItem): void => {
						this.messageService.showToaster(
							's_4',
							'productsPackages',
							data.name
						);
					}
				);
				break;
			case 'Duplicate Product':
				this.addNewProduct(props).subscribe(
					(data: IProductPackageItem): void => {
						this.messageService.showToaster(
							's_6',
							'productsPackages',
							data.name
						);
					}
				);
				break;
			case 'Edit Product':
				this.updateProduct(props).subscribe(
					(data: IProductPackageItem): void => {
						this.messageService.showToaster(
							's_7',
							'productsPackages',
							data.name
						);
					}
				);
				break;
			case 'Delete':
				this.deleteProduct(props).subscribe((): void => {
					this.messageService.showToaster(
						's_5',
						'productsPackages',
						props.name
					);
				});
				break;
			default:
				break;
		}
	}

	private addNewProduct(
		dataToSend: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesDataService
			.createPackagesOrProducts(dataToSend)
			.map((data: IProductPackageItem) =>
				this.resetProductDataAfterAction(data)
			);
	}

	private updateProduct(
		dataToSend: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesDataService
			.updatePackagesOrProducts(dataToSend.id, dataToSend)
			.map((data: IProductPackageItem) =>
				this.resetProductDataAfterAction(data)
			);
	}

	private deleteProduct(
		props: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesDataService
			.deletePackagesOrProducts(props.id)
			.map((data: IProductPackageItem) =>
				this.resetProductDataAfterAction(data)
			);
	}

	private getProductsData(): void {
		this.productPackagesDataService
			.getPackagesOrProducts(this.pageRequest)
			.subscribe((data: IItemsOverviewGen<IProductPackageItem>) =>
				this.successGetProductsData(data)
			);
	}

	private successGetProductsData(
		data: IItemsOverviewGen<IProductPackageItem>
	): void {
		this.products = JSON.parse(JSON.stringify(data.items));
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pageNumbers = this.paginationService.calculatePageNumbers(
			this.itemsQuantity,
			this.pageRequest.paging.pageSize
		);
		this.pageRequest.paging.searchToken = data.searchToken;
		this.allowedFilters = data.sortingFields;
		this.setPageProductToLocalStorage(this.pageRequest);
	}

	private resetProductDataAfterAction(
		data: IProductPackageItem
	): IProductPackageItem {
		this.pageRequest.paging.searchToken = null;
		this.pageRequest.paging.pageNumber = 0;
		this.getProductsData();
		this.modalDataSubscription.unsubscribe();
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
		this.selectedPackageModalRef.close();
		return data;
	}

	private subscribeToModalData(): void {
		this.selectedPackageModalRef.result.then(
			(): void => this.modalDataSubscription.unsubscribe(),
			(): void => this.modalDataSubscription.unsubscribe()
		);

		this.modalDataSubscription = this.modalService
			.getModalData()
			.filter(data => data && data.action && data.props)
			.subscribe((data: IModalAction<IProductPackageItem>) =>
				this.productModalActions(data)
			);
	}

	// LOCAL STORAGE
	private applyPageProductFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`settingsProductRequest_${this.companyId}`)
		);
		this.pageRequest = this.PRODUCTS_REQUEST_DEFAULT;

		if (pageRequestObject && pageRequestObject !== '') {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.sortCriterias[0].fieldName = fieldName;
			this.pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setPageProductToLocalStorage(pageRequest: PageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`settingsProductRequest_${this.companyId}`);
		localStorage.setItem(
			`settingsProductRequest_${this.companyId}`,
			pageRequestString
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/packages-product/product-block/product-block.component.ts